/**
 * B2B VAT form + checkout guard.
 *
 * Config and translations come from window.vatFormConfig (rendered by
 * snippets/cart-vat-config.liquid in theme.liquid), so this script works on
 * every page — including cart drawer checkouts outside the cart page.
 *
 * The checkout interception uses one document-level capture listener instead
 * of per-button listeners: cart re-renders replace the summary DOM (including
 * the checkout button), which used to silently drop the validation handlers.
 */
(() => {
  if (window.vatFormGuardInstalled) return;
  window.vatFormGuardInstalled = true;

  const config = () => window.vatFormConfig || { strings: {}, customer: {} };
  const strings = () => config().strings || {};

  const getSecondsText = (count) => {
    if (count === 1) return strings().seconds1 || '';
    if (count >= 2 && count <= 4) return strings().seconds24 || '';
    return strings().seconds5Plus || '';
  };

  const emailLooksValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Customers type VAT IDs as "de 190.439-671"; VIES accepts only the bare
  // upper-case form, so every path (form, cart attributes, B2B metafield)
  // runs the value through this before validating or persisting it.
  const normalizeDic = (value) => String(value || '').toUpperCase().replace(/[\s.\-]/g, '');

  // Message helpers use the cart form elements when present and fall back to
  // alert() on pages without the form (drawer checkout on product pages).
  const ui = {
    get errorEl() { return document.getElementById('vat-error'); },
    get infoEl() { return document.getElementById('vat-info'); },
    showError(html, plainText) {
      if (this.errorEl) {
        this.errorEl.innerHTML = html;
        this.errorEl.style.display = 'block';
      } else {
        alert(plainText || html.replace(/<[^>]*>/g, ''));
      }
    },
    reset() {
      if (this.errorEl) this.errorEl.style.display = 'none';
      if (this.infoEl) this.infoEl.style.display = 'none';
    },
    async countdown(template) {
      const delaySeconds = 5;
      for (let seconds = delaySeconds; seconds > 0; seconds--) {
        if (this.infoEl) {
          this.infoEl.style.display = 'block';
          this.infoEl.textContent = (template || '').replace(/\{\{\s*seconds\s*\}\}/g, `${seconds} ${getSecondsText(seconds)}`);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (this.infoEl) this.infoEl.style.display = 'none';
    }
  };

  async function validateVatNumber(countryCode, vatNumber) {
    // VIES often answers MS_MAX_CONCURRENT_REQ several times in a row (FR, DE); with only
    // 3 attempts customers saw "invalid VAT number" for a valid one
    const maxAttempts = 5;
    let vatData = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`VAT validation attempt ${attempt}/${maxAttempts}`);
        const response = await fetch(config().validationUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            countryCode,
            vatNumber,
            requesterMemberStateCode: countryCode,
            requesterNumber: vatNumber
          })
        });
        vatData = await response.json();

        const serverBusy = vatData.errorWrappers?.some((err) => err.error === 'MS_MAX_CONCURRENT_REQ');
        if (serverBusy && attempt < maxAttempts) {
          await ui.countdown(strings().serverBusy);
          continue;
        }
        break;
      } catch (error) {
        console.error('VAT validation request error:', error);
        if (attempt === maxAttempts) return { ok: false, reason: 'error' };
        await ui.countdown(strings().connectionError);
      }
    }

    if (!vatData || !vatData.valid) return { ok: false, reason: 'invalid' };
    return { ok: true };
  }

  async function setTaxExempt(email, dic) {
    try {
      // locale = storefront language, so a customer created here gets Shopify e-mails in it
      const url = `${config().taxExemptUrl}?email=${encodeURIComponent(email)}&vatNumber=${encodeURIComponent(dic)}&locale=${encodeURIComponent(config().locale || '')}`;
      const response = await fetch(url);
      const data = await response.json();
      console.log('Tax exempt response:', data);
      return response.ok;
    } catch (error) {
      console.error('Error setting tax exempt:', error);
      return false;
    }
  }

  /**
   * Validates the DIČ and sets the exemption for the given email.
   * Returns true when checkout may proceed. Unlike the old flow, a failed
   * tax-exempt call now blocks checkout instead of silently continuing.
   */
  async function validateIdentity(dic, email, { accountLink = false } = {}) {
    dic = normalizeDic(dic);
    const s = strings();
    const link = accountLink ? ` <a href="/account" style="text-decoration: underline;">${s.updateInAccount || ''}</a>` : '';

    const countryCodeMatch = dic.match(/^([A-Z]{2})/);
    if (!countryCodeMatch) {
      ui.showError((s.vatPrefixRequired || '') + link, s.vatPrefixRequired);
      return false;
    }

    // Domácí CZ DIČ: bez validace i bez osvobození (reverse charge se neaplikuje)
    if (countryCodeMatch[1] === 'CZ') return true;

    const result = await validateVatNumber(countryCodeMatch[1], dic.substring(2));
    if (!result.ok) {
      const message = result.reason === 'invalid' ? s.vatInvalid : s.validationError;
      ui.showError((message || '') + link, message);
      return false;
    }

    if (!email || !(await setTaxExempt(email, dic))) {
      const message = email ? s.validationError : s.emailRequired;
      ui.showError((message || '') + link, message);
      return false;
    }

    return true;
  }

  // Guest flow: values come from the form inputs
  async function validateFromForm() {
    const vatInput = document.getElementById('vat-input');
    const emailInput = document.getElementById('email-input');
    if (!vatInput || !emailInput) return true;

    const s = strings();
    ui.reset();
    vatInput.style.borderColor = '';
    emailInput.style.borderColor = '';

    const emailValue = emailInput.value.trim();
    if (!emailLooksValid(emailValue)) {
      ui.showError(s.emailRequired || '', s.emailRequired);
      emailInput.style.borderColor = 'red';
      return false;
    }

    let vatValue = normalizeDic(vatInput.value);
    if (!vatValue || vatValue.length < 3) {
      ui.showError(s.vatFillRequired || '', s.vatFillRequired);
      vatInput.style.borderColor = 'red';
      return false;
    }

    // Auto-fix duplicated country prefix (e.g. DEDE123 -> DE123)
    const vatPrefix = vatValue.substring(0, 2);
    if (vatValue.length > 4 && vatValue.substring(2, 4) === vatPrefix) {
      vatValue = vatPrefix + vatValue.substring(4);
      vatInput.value = vatValue;
    }

    const valid = await validateIdentity(vatValue, emailValue);
    if (!valid) vatInput.style.borderColor = 'red';
    return valid;
  }

  function proceedToCheckout(form) {
    form.action = '/checkout';
    form.method = 'POST';
    form.submit();
  }

  async function redirectViaPermalink(form, cartData, checkoutEmail, values, b2bCode) {
    try {
      const itemsStr = cartData.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
      if (!itemsStr) {
        // Cart is empty - submitting to /checkout would create an empty
        // checkout that bounces the customer to the homepage. Abort instead.
        console.warn('Cart is empty, aborting checkout submission');
        return false;
      }

      // Build URL manually (URLSearchParams encodes [] which Shopify may not parse)
      const parts = [];
      parts.push('checkout[email]=' + encodeURIComponent(checkoutEmail));
      if (values.firma) parts.push('checkout[shipping_address][company]=' + encodeURIComponent(values.firma));
      if (values.dic) parts.push('attributes[dic]=' + encodeURIComponent(values.dic));
      if (values.ico) parts.push('attributes[ico]=' + encodeURIComponent(values.ico));
      if (values.firma) parts.push('attributes[firma]=' + encodeURIComponent(values.firma));
      if (values.email) parts.push('attributes[email]=' + encodeURIComponent(values.email));

      if (cartData.attributes) {
        for (const [key, value] of Object.entries(cartData.attributes)) {
          if (value && !parts.some((p) => p.startsWith('attributes[' + key + ']='))) {
            parts.push('attributes[' + key + ']=' + encodeURIComponent(String(value)));
          }
        }
      }

      if (cartData.note) parts.push('note=' + encodeURIComponent(cartData.note));
      // The permalink builds a new cart, so the B2B price list code must travel with it
      if (b2bCode) parts.push('discount=' + encodeURIComponent(b2bCode));

      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      window.location.href = '/cart/' + itemsStr + '?' + parts.join('&');
      return true;
    } catch (error) {
      console.error('Cart permalink failed:', error);
      proceedToCheckout(form);
      return true;
    }
  }

  async function runCheckoutFlow(button, form) {
    const restore = () => {
      window.vatCheckoutInFlight = false;
      button.disabled = false;
      button.classList.remove('vat-validation-loading');
      button.style.opacity = '1';
      button.style.pointerEvents = 'all';
    };

    button.classList.add('vat-validation-loading');

    // B2B price list (assets/b2b-cart.js, only for customers with a price list): the discount code
    // is recalculated from the final cart before leaving for checkout
    let b2bCode = null;
    if (window.b2bCart) {
      const b2bResult = await window.b2bCart.beforeCheckout();
      b2bCode = (b2bResult && b2bResult.code) || null;
    }

    let cartData;
    try {
      cartData = await (await fetch('/cart.js')).json();
    } catch (error) {
      // Fail open: never leave the customer unable to check out
      console.error('Failed to read cart, proceeding without validation:', error);
      proceedToCheckout(form);
      return;
    }

    const attrs = cartData.attributes || {};
    const customer = config().customer || {};
    const isRegistered = !!(customer.dic && customer.firma && customer.email);
    const checkbox = document.getElementById('company-purchase-checkbox');

    const readInput = (id) => (document.getElementById(id)?.value || '').trim();

    let valid;
    let checkoutEmail = '';
    let values = null;

    if (checkbox) {
      // Cart page: the checkbox is the source of truth
      if (!checkbox.checked) {
        proceedToCheckout(form);
        return;
      }
      if (isRegistered) {
        valid = await validateIdentity(customer.dic, customer.email, { accountLink: true });
        checkoutEmail = customer.email;
        values = { dic: customer.dic, ico: customer.ico || '', firma: customer.firma, email: customer.email };
      } else {
        valid = await validateFromForm();
        checkoutEmail = readInput('email-input');
        values = {
          dic: readInput('vat-input'),
          ico: readInput('ico-input'),
          firma: readInput('firma-input'),
          email: readInput('email-input')
        };
      }
    } else if (attrs.dic && String(attrs.dic).trim()) {
      // No form on this page (drawer checkout) — use the saved cart attributes
      values = {
        dic: normalizeDic(attrs.dic),
        ico: String(attrs.ico || '').trim(),
        firma: String(attrs.firma || '').trim(),
        email: String(attrs.email || customer.email || '').trim()
      };
      valid = await validateIdentity(values.dic, values.email, { accountLink: isRegistered });
      checkoutEmail = values.email;
    } else if (isRegistered) {
      // Registered B2B customer checking out from the drawer before visiting the cart
      valid = await validateIdentity(customer.dic, customer.email, { accountLink: true });
      checkoutEmail = customer.email;
      values = { dic: customer.dic, ico: customer.ico || '', firma: customer.firma, email: customer.email };
    } else {
      proceedToCheckout(form);
      return;
    }

    if (!valid) {
      restore();
      return;
    }

    if (values) values.dic = normalizeDic(values.dic);

    if (checkoutEmail) {
      const redirected = await redirectViaPermalink(form, cartData, checkoutEmail, values, b2bCode);
      if (!redirected) restore();
    } else {
      proceedToCheckout(form);
    }
  }

  document.addEventListener('click', (e) => {
    const button = e.target.closest('button[name="checkout"]');
    if (!button) return;

    // Without config the guard cannot validate — never trap the customer
    if (!config().validationUrl) return;

    // Global single-flight lock: the page contains several checkout buttons
    // (cart page + drawer); a second concurrent run reads the cart after the
    // first run cleared it and would submit an empty checkout.
    if (window.vatCheckoutInFlight) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    if (button.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const cartTermsCheckbox = document.querySelector('.js-cart-terms-checkbox');
    if (cartTermsCheckbox && !cartTermsCheckbox.checked) {
      return; // Let the cart-terms.js handler show its alert
    }

    const form = button.form || button.closest('form');
    if (!form) {
      console.error('No form found for checkout button');
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();

    window.vatCheckoutInFlight = true;
    button.disabled = true;
    button.style.opacity = '0.5';
    button.style.pointerEvents = 'none';

    runCheckoutFlow(button, form);
  }, true);

  /**
   * The form itself: re-created on every cart re-render, so all listeners are
   * wired in connectedCallback (custom elements upgrade on innerHTML, inline
   * scripts do not run).
   */
  class CartVatForm extends HTMLElement {
    static normalizeDic(value) {
      return normalizeDic(value);
    }

    static validateVatNumber(countryCode, vatNumber) {
      return validateVatNumber(countryCode, vatNumber);
    }

    static setTaxExempt(email, dic) {
      return setTaxExempt(email, dic);
    }

    connectedCallback() {
      this.checkbox = this.querySelector('#company-purchase-checkbox');
      this.formBlock = this.querySelector('.cart-vat-form');
      this.vatInput = this.querySelector('#vat-input');
      this.icoField = this.querySelector('#ico-field');
      this.icoInput = this.querySelector('#ico-input');
      this.firmaInput = this.querySelector('#firma-input');
      this.emailInput = this.querySelector('#email-input');

      const customer = config().customer || {};
      this.isRegistered = !!(customer.dic && customer.firma && customer.email);

      this.updateIcoVisibility();
      this.vatInput?.addEventListener('input', () => this.updateIcoVisibility());
      this.checkbox?.addEventListener('change', (e) => this.onToggle(e));

      [this.vatInput, this.icoInput, this.firmaInput, this.emailInput].forEach((input) => {
        input?.addEventListener('change', () => this.persistAttributes());
      });

      if (this.isRegistered) this.syncRegisteredAttributes(customer);
      this.updateExpressButtons();
    }

    // Express checkout buttons (PayPal, Shop Pay, ...) submit the cart form
    // straight to checkout and bypass the VAT guard, so they are hidden
    // while the customer buys as a company. B2C keeps them.
    updateExpressButtons(hide = !!this.checkbox?.checked) {
      document.querySelectorAll('[data-vat-express-buttons]').forEach((el) => {
        el.style.display = hide ? 'none' : '';
      });
    }

    updateIcoVisibility() {
      if (!this.vatInput || !this.icoField) return;
      const vatValue = this.vatInput.value.trim();
      // An empty field counts as the market country (the old prefill did the
      // same implicitly), so the CZ shop shows IČO before anything is typed.
      const effectiveValue = vatValue || (config().countryCode || '');
      const isCZ = effectiveValue.toUpperCase().startsWith('CZ');
      this.icoField.style.display = isCZ ? 'block' : 'none';

      // Upozorneni na e-mail se tyka jen osvobozeni od DPH (reverse charge),
      // ktere se u CZ DIC neaplikuje - zobrazit jen pro zahranicni DIC
      const emailTaxNotice = this.querySelector('#email-tax-notice');
      if (emailTaxNotice) {
        emailTaxNotice.style.display = vatValue.length > 0 && !isCZ ? 'flex' : 'none';
      }
    }

    async onToggle(e) {
      this.updateExpressButtons(!!e.target.checked);
      if (e.target.checked) {
        if (this.isRegistered) {
          if (this.formBlock) this.formBlock.style.display = 'none';
        } else {
          if (this.formBlock) this.formBlock.style.display = 'block';
          // Only hint the country prefix via the placeholder. Pre-filling the
          // value made an untouched field look filled: the bare code (e.g. "DE")
          // was then submitted as attributes[dic] by the cart form and ended up
          // on orders without any VAT number.
          if (this.vatInput && config().countryCode) {
            this.vatInput.placeholder = config().countryCode;
          }
          this.updateIcoVisibility();
        }
      } else {
        if (this.formBlock) this.formBlock.style.display = 'none';
        [this.vatInput, this.icoInput, this.firmaInput].forEach((input) => {
          if (input) input.value = '';
        });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'attributes[ico]': '',
            'attributes[dic]': '',
            'attributes[firma]': '',
            'attributes[email]': ''
          })
        });
      }
    }

    // Persisting typed values into cart attributes keeps them alive across
    // cart re-renders (the re-rendered form is prefilled from cart.attributes)
    async persistAttributes() {
      if (!this.checkbox?.checked) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'attributes[dic]': normalizeDic(this.vatInput?.value),
          'attributes[ico]': this.icoInput?.value.trim() || '',
          'attributes[firma]': this.firmaInput?.value.trim() || '',
          'attributes[email]': this.emailInput?.value.trim() || ''
        })
      });
    }

    async syncRegisteredAttributes(customer) {
      try {
        const cartData = await (await fetch('/cart.js')).json();
        const attrs = cartData.attributes || {};
        const updates = {};
        if (customer.ico && !attrs.ico) updates['attributes[ico]'] = customer.ico;
        if (customer.dic && !attrs.dic) updates['attributes[dic]'] = normalizeDic(customer.dic);
        if (customer.firma && !attrs.firma) updates['attributes[firma]'] = customer.firma;
        if (customer.email && !attrs.email) updates['attributes[email]'] = customer.email;
        if (Object.keys(updates).length > 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates)
          });
        }
      } catch (error) {
        console.error('Failed to sync registered customer attributes:', error);
      }
    }
  }

  if (!customElements.get('cart-vat-form')) {
    customElements.define('cart-vat-form', CartVatForm);
  }
})();
